import { AutocompleteOption } from './autocomplete'
import { FileMetadata } from './files'
import { Coordinates } from './places'
import {
  Language,
  Balcony,
  Rooms,
  Kitchen,
  Condition,
  Parking,
  Sauna,
  Storage,
  CarHeatingSocket,
  Dishwasher,
  LaundryMachine,
  Heating,
  BuildingType,
  WaterFeeBasis,
  SecurityDeposit,
  RentPeriod,
  PetsAllowed,
  AnnouncementType
} from './propValues/apartments'
import { ShowingType } from './showings'

export interface CreateApartment {
  apartmentId: string
  userId: string
  createdAt: number
}

/** passed on to step 0 in platform-ui */
export interface ClientContactInfo {
  name: string
  phoneNumber: string
}

/** will be deleted soon */
export interface ContactInfo {
  name?: string
  email?: string
  phoneNumber?: string
  language?: Language
}

export interface MainOwner extends ContactInfo {
  /** Extracted from Google Places API and saved in form*/
  coordinates?: Coordinates
  companyName?: string
  businessId?: string
  /** Extracted from Google Places API */
  googlePlaceId: string
  /** Extracted from Google Places API */
  streetAddress: string
  /** address that contains streetAddress, buildingLetter and apartmentNumber */
  streetNumber?: number
  district?: string
  address: string
  postalCode: string
  postalArea: string
  buildingLetter?: string
  apartmentNumber?: number
  iban?: string
  clientName?: string
  clientEmail?: string
  clientPhone?: string
  role?: string
}

export interface OtherOwner {
  name: string
  email: string
}

export interface ApartmentClientInformation {
  mainOwner: MainOwner
  otherOwners?: OtherOwner[]
  promocode?: string
}

export interface AddressInfoInput {
  /** Client only prop for Google Places API. Do not save this to db. */
  autocompleteLocation: AutocompleteOption | string
  floor?: number
  floors?: number
  /** Extracted from Google Places API or manually corrected */
  postalCode: string
  /** Extracted from Google Places API or manually corrected */
  postalArea: string
  streetNumber?: number
  district?: string
  buildingLetter?: string
  apartmentNumber?: number
}

export interface AddressInfo
  extends Omit<AddressInfoInput, 'autocompleteLocation'> {
  /** Extracted from Google Places API */
  streetAddress: string
  /** address that contains streetAddress, buildingLetter and apartmentNumber */
  address: string
  /** Extracted from Google Places API and saved in form*/
  googlePlaceId: string
  /** Extracted from Google Places API and saved in form*/
  coordinates?: Coordinates
}

export interface BuildingInfo {
  buildingType: BuildingType
  buildYear: number
  storage: Storage
}

export interface PropertyInfo {
  squareMeters: number
  condition: Condition
  rooms: Rooms
  kitchen: Kitchen
  balcony: Balcony
  price?: string
}

export const CheckboxPropertyKeys = {
  renovatedCheckBoxProperty: 'renovatedCheckBoxProperty' as const,
  hasElevator: 'hasElevator' as const,
  isFurnished: 'isFurnished' as const,
  firstRentDiscount50PercentCheckBoxProperty: 'firstRentDiscount50PercentCheckBoxProperty' as const,
  hasBuiltInClosetCheckBoxProperty: 'hasBuiltInClosetCheckBoxProperty' as const,
  petsAllowedCheckBoxProperty: 'petsAllowedCheckBoxProperty' as const,
  hasSaunaCheckBoxProperty: 'hasSaunaCheckBoxProperty' as const,
  housingPropertySauna: 'housingPropertySauna' as const,
  internetIncluded: 'internetIncluded' as const,
  newProperty: 'newProperty' as const,
  districtHeatingCheckBoxProperty: 'districtHeatingCheckBoxProperty' as const,
  electricHeatingCheckBoxProperty: 'electricHeatingCheckBoxProperty' as const
}

export interface CheckBoxProperties {
  renovatedCheckBoxProperty?: boolean
  hasElevator?: boolean
  isFurnished?: boolean
  hasSaunaCheckBoxProperty?: boolean
  housingPropertySauna?: boolean
  hasBuiltInClosetCheckBoxProperty?: boolean
  petsAllowedCheckBoxProperty?: boolean
  internetIncluded?: boolean
  firstRentDiscount50PercentCheckBoxProperty?: boolean
  newProperty?: boolean
  districtHeatingCheckBoxProperty?: boolean
  electricHeatingCheckBoxProperty?: boolean
}

export interface AdditionalProperties {
  sauna: Sauna
  parking: Parking
  carHeatingSocket?: CarHeatingSocket // only asked if parking answered with anything other than 'not sure' or 'no'
  dishwasher: Dishwasher
  laundryMachine: LaundryMachine
  heating: Heating
  heatingDescription?: string // if heating === 'other'
  petsAllowed: PetsAllowed
}

export interface ApartmentInformation
  extends AddressInfo,
    PropertyInfo,
    BuildingInfo,
    AdditionalProperties,
    CheckBoxProperties {
  propertyManager?: string
  renovations?: string
  notes: string
  damages?: string
  // for SWTITCHED DATA
  price?: any
  availableFromDate?: string
  waterFeeBasis?: WaterFeeBasis
  waterFee?: number
  energyCertification: string
}

export interface InstantRentAuthInformation {
  apartmentId?: string
  readyForMoveDate: string | Date
  movingWithAmount: number | any
  acceptedTc: boolean
  language: string
}

export type DropDownItem = {
  label: string
  value: unknown
}

export interface InstantRentInformation {
  readyForMoveDate?: string
  movingWithAmount?: DropDownItem
}
export interface Credentials {
  email: string
  name: string
  password: string
  phoneNumber: string
  isSVAMember: boolean
}
export interface ApartmentConfirmation {
  /** ISO date string */
  availableFromDate?: string // if not defined, per contract
  /** ISO date string example 2020/12/24 */
  availableUntilDate?: string
  rentPeriod: RentPeriod
  securityDeposit: SecurityDeposit
  securityDepositAmount?: number // not required, if securityDeposit === 'other'
  price: number
  waterFeeBasis: WaterFeeBasis
  /** shown if waterFeeBasis !== 'fixed',
   * Required if waterFeeBasis === 'perTenant' or 'perApartment',
   *
   * Shown but not required if waterFeeBasis === 'metered' or 'notSure' */
  waterFee?: number
  parkingFee?: number // not required, only shown if parking !== 'no' or 'not sure'
  requirePriceEstimate?: boolean
  showingType: ShowingType
  announcementType?: AnnouncementType
  promocode?: string
  confirm: boolean
  notes?: string

  email?: string
  name?: string
  password?: string
  phoneNumber?: string
  isSVAMember?: string
}

export const ApartmentStatus = {
  created: 'created' as const,
  confirmed: 'confirmed' as const,
  marketing: 'marketing' as const,
  followUp: 'followUp' as const,
  rentContractWanted: 'rentContractWanted' as const,
  rented: 'rented' as const,
  cancelled: 'cancelled' as const
}

export type Status =
  | typeof ApartmentStatus.created
  | typeof ApartmentStatus.confirmed
  | typeof ApartmentStatus.marketing
  | typeof ApartmentStatus.followUp
  | typeof ApartmentStatus.rentContractWanted
  | typeof ApartmentStatus.rented
  | typeof ApartmentStatus.cancelled

export interface ApartmentAdminInfo {
  adminNotes: string
}

export interface UpdateApartmentUserIdProps {
  /** new owners userId */
  newUserId: string
}

export interface Metadata {
  created: boolean
  /** user email not confirmed on apartment creation */
  createdBeforeLogin?: boolean
  /** user logs in on beforeLoginStepper confirmation step and uses addAndConfirm function */
  createdOnLogin?: boolean
  client?: boolean
  /** this is used to check if the user data should not be pulled from apartment.client, but from users-api */
  clientContactInfoInUsersDB?: boolean
  information?: boolean
  confirmed?: boolean
  media?: boolean
  showings?: boolean
  marketing?: boolean
  adminInfo?: boolean
  showingReserved?: boolean
  followUp?: boolean
  userIdUpdated?: boolean
  contractRent?: boolean
  tenant?: boolean
  cancelled?: boolean
}

export interface ApartmentApplicationIds {
  contractWantedId?: string
  oldContractWantedIds: string[]
  tenantId?: string
  oldTenantIds: string[]
}

export interface ApartmentMediaItem {
  id: string
  key: string
  description?: string
}

export type ApartmentMedia = Array<ApartmentMediaItem>

export interface Apartment {
  apartmentId: string
  userId: string
  /** UNIX epoch */
  createdAt: number
  /** UNIX epoch */
  updatedAt?: number
  /** UNIX epoch */
  applicationsViewedAt?: number
  /** UNIX epoch */
  statusUpdatedAt?: number
  media?: ApartmentMedia
  metadata: Metadata
  client?: ApartmentClientInformation
  information?: ApartmentInformation
  confirmation: ApartmentConfirmation
  switchedData?: any
  applicationIds?: ApartmentApplicationIds
  adminInfo?: ApartmentAdminInfo
  status: Status
  code: string
}
export interface SwitchedData {
  price?: string
  availableFromDate?: string
  waterFeeBasis?: WaterFeeBasis
  waterFee?: number
}
export interface BeforeLoginApartment {
  createdAt: number
  metadata: Metadata
  information?: ApartmentInformation
  confirmation?: any
  updatedAt?: number
  switchedData?: SwitchedData
  credentials?: Credentials
}

export interface AddBeforeLoginApartment extends BeforeLoginApartment {
  userId: string
  filesToUploadMetadata?: FileMetadata[]
}
export interface ReserveApartmentData {
  apartmentId: string
  address: string
  addressFull?: string
  rooms: string
  kitchen: string
  price: number
}
